"use client";

import Link from "next/link";
import { CheckCircle2 } from "lucide-react";
import { AgentReadyUrlForm } from "~/components/siteglue/agent-ready/scan-form";
import { FadeIn, HomeEyebrow, HomeHeading, HomeLead, HomeSection } from "~/components/siteglue/home/section-shell";
import { AUDIT_INDEX, AUDIT_SERVICE_PAGES, type AuditPageContent } from "~/lib/siteglue/audits/catalog";
import { AUDIT_AGENTS_TEST_HREF, BENCHMARK_LIBRARY_HREF, HOMEPAGE_URL_PLACEHOLDER } from "~/lib/siteglue/landing-redesign/copy";

export function AuditExplainerPage({ page }: { page: AuditPageContent }) {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <HomeSection tone="brand" className="overflow-hidden pb-16 pt-16 sm:pb-20 sm:pt-24">
        <FadeIn className="relative mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#9cbb5a]">{page.eyebrow}</p>
          <h1 className="mt-4 text-balance text-4xl font-bold tracking-tight text-white sm:text-5xl">{page.title}</h1>
          <p className="mx-auto mt-3 text-xl font-medium text-white">{page.headline}</p>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">{page.description}</p>
          <div className="mx-auto mt-9 max-w-xl text-left text-slate-900">
            <AgentReadyUrlForm
              className="mx-0 mt-0 max-w-none"
              urlPlaceholder={HOMEPAGE_URL_PLACEHOLDER}
              stepZeroCta={page.ctaLabel}
              hideSteps
              urlOnly
              fixedReports={page.scanReports}
            />
          </div>
          {page.secondaryCta ? (
            <Link href={page.secondaryCta.href} className="mt-5 inline-flex text-sm font-semibold text-slate-200 underline-offset-4 hover:underline">
              {page.secondaryCta.label}
            </Link>
          ) : null}
        </FadeIn>
      </HomeSection>

      <HomeSection tone="white">
        <HomeEyebrow>What is this audit?</HomeEyebrow>
        <HomeHeading>{page.title}</HomeHeading>
        <HomeLead>{page.what}</HomeLead>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-700">{page.measures}</p>
      </HomeSection>

      <HomeSection tone="slate">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <HomeEyebrow>Why this audit matters</HomeEyebrow>
            <p className="mt-4 text-base leading-relaxed text-slate-700">{page.why}</p>
          </div>
          <div>
            <HomeEyebrow>Who it is for</HomeEyebrow>
            <p className="mt-4 text-base leading-relaxed text-slate-700">{page.who}</p>
          </div>
        </div>
      </HomeSection>

      <HomeSection tone="white">
        <HomeEyebrow>How SiteGlue evaluates the website</HomeEyebrow>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-700">{page.methodology}</p>
        <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <h2 className="text-base font-semibold text-slate-900">The formula behind the score</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">{page.formula}</p>
          {page.plannedNote ? <p className="mt-3 text-sm leading-relaxed text-slate-600">{page.plannedNote}</p> : null}
        </div>
      </HomeSection>

      {page.benchmarks?.length ? (
        <HomeSection tone="slate">
          <HomeEyebrow>Key benchmarks and metrics</HomeEyebrow>
          <HomeHeading>What we score</HomeHeading>
          <HomeLead>
            Each card opens the Audit Benchmark Library. Scanner, reports, and this page share the same definitions.
          </HomeLead>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {page.benchmarks.map((item) => (
              <Link
                key={item.title}
                href={item.href ?? BENCHMARK_LIBRARY_HREF}
                className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-slate-300"
              >
                <h2 className="text-base font-semibold text-slate-900">{item.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.body}</p>
              </Link>
            ))}
          </div>
        </HomeSection>
      ) : null}

      {page.channels?.length ? (
        <HomeSection tone="white">
          <HomeEyebrow>Channels</HomeEyebrow>
          <HomeHeading>Views of the same CRO score</HomeHeading>
          <HomeLead>Six channels are inspection views, not a third public score. Scheduling is a Conversion Flow step.</HomeLead>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {page.channels.map((item) => (
              <div key={item.title} className="rounded-2xl border border-slate-200 bg-white p-5">
                <h2 className="text-base font-semibold text-slate-900">{item.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.body}</p>
              </div>
            ))}
          </div>
        </HomeSection>
      ) : null}

      {page.examples?.length ? (
        <HomeSection tone="slate">
          <HomeEyebrow>Passing and failing checks</HomeEyebrow>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {page.examples.map((item) => (
              <div key={item.pass} className="rounded-2xl border border-slate-200 bg-white p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">Pass</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">{item.pass}</p>
                <p className="mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-red-700">Fail</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">{item.fail}</p>
              </div>
            ))}
          </div>
        </HomeSection>
      ) : null}

      <HomeSection tone="white">
        <HomeEyebrow>Recommended improvements</HomeEyebrow>
        <ul className="mt-6 space-y-3">
          {page.improvements.map((item) => (
            <li key={item} className="flex items-start gap-2.5 text-sm text-slate-700">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#9cbb5a]" aria-hidden />
              {item}
            </li>
          ))}
        </ul>
        <ul className="mt-8 space-y-3">
          {page.findings.map((item) => (
            <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#4f81bd]" aria-hidden />
              {item}
            </li>
          ))}
        </ul>
      </HomeSection>

      <HomeSection tone="slate">
        <HomeEyebrow>Frequently asked questions</HomeEyebrow>
        <div className="mt-8 space-y-6">
          {page.faqs.map((faq) => (
            <div key={faq.q}>
              <h2 className="text-base font-semibold text-slate-900">{faq.q}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">{faq.a}</p>
            </div>
          ))}
        </div>
        <div className="mx-auto mt-10 max-w-xl text-left">
          <AgentReadyUrlForm
            className="mx-0 mt-0 max-w-none"
            urlPlaceholder={HOMEPAGE_URL_PLACEHOLDER}
            stepZeroCta={page.ctaLabel}
            hideSteps
            urlOnly
            fixedReports={page.scanReports}
          />
        </div>
        {page.slug === "agent-readiness" ? (
          <p className="mt-4 text-sm text-slate-600">
            Optional:{" "}
            <Link href={AUDIT_AGENTS_TEST_HREF} className="font-semibold text-[#4f81bd] hover:underline">
              Test With AI Agents
            </Link>
          </p>
        ) : null}
      </HomeSection>
    </div>
  );
}

export function AuditsIndexPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <HomeSection tone="brand" className="overflow-hidden pb-16 pt-16 sm:pb-20 sm:pt-24">
        <FadeIn className="relative mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#9cbb5a]">SiteGlue</p>
          <h1 className="mt-4 text-balance text-4xl font-bold tracking-tight text-white sm:text-5xl">
            {AUDIT_INDEX.title}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">{AUDIT_INDEX.description}</p>
        </FadeIn>
      </HomeSection>
      <HomeSection tone="white">
        <div className="grid gap-6 md:grid-cols-3">
          {AUDIT_SERVICE_PAGES.map((page) => (
            <Link
              key={page.slug}
              href={page.href}
              className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-300"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{page.eyebrow}</p>
              <h2 className="mt-2 text-xl font-semibold text-slate-900">{page.title}</h2>
              <p className="mt-2 text-base font-medium text-slate-800">{page.headline}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{page.description}</p>
              <p className="mt-4 text-sm font-semibold text-[#4f81bd]">{page.ctaLabel}</p>
            </Link>
          ))}
        </div>
      </HomeSection>
    </div>
  );
}
